import React, { useEffect, useState } from 'react';
import { postApi, getApi } from '../../services/api';
import { toast } from 'react-toastify';

/* ── Server-side capping logic (mirrored from backend) ────────────────────
   $capping = ($request->stake_amount < 3001) ? 2 : 2.5;
   $totalIncome           = stake_amount * capping
   $dailyCreditReward     = totalIncome / 720
   $dailyRewardPercentage = (capping / 720) * 100
──────────────────────────────────────────────────────────────────────── */
const calcStakePreview = (amount) => {
  const amt = parseFloat(amount) || 0;
  if (amt <= 0) return null;
  const capping = amt < 3001 ? 2 : 2.5;
  const totalIncome = amt * capping;
  const dailyCredit = totalIncome / 720;
  const dailyPct = (capping / 720) * 100;
  const roi = (capping * 100).toFixed(0);          // e.g. 200% or 250%
  return {
    capping,
    totalIncome: totalIncome.toFixed(2),
    dailyCredit: dailyCredit.toFixed(4),
    dailyPct: dailyPct.toFixed(4),
    roi: `${roi}%`,
    dailyRoiPct: `${dailyPct.toFixed(4)}%`,
    period: '720 days',
    plan: 'Gold',
  };
};

const Stake = () => {
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStake, setSelectedStake] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [stakedata, setStakedata] = useState({ data: [], stake_amount: 0, stake_income: 0, capping_income: 0, remain_income: 0 });
  const [availableDeposit, setAvailableDeposit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const preview = calcStakePreview(stakeAmount);

  const analytics = [
    { title: 'Stake Income', value: `$${stakedata?.stake_income || 0}`, icon: 'bi-layers', color: 'green' },
    { title: 'Capping Income', value: `$${stakedata?.capping_income || 0}`, icon: 'bi-shield', color: 'blue' },
    { title: 'Total Income', value: `$${stakedata?.capping_income || 0}`, icon: 'bi-bar-chart', color: 'yellow' },
    { title: 'Remain Income', value: `$${stakedata?.remain_income || 0}`, icon: 'bi-wallet2', color: 'purple' },
  ];

  useEffect(() => {
    fetchActiveStakes();
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Profile: get available_deposit ── */
  const fetchProfile = async () => {
    try {
      const res = await getApi('profile');
      const user = res.data?.USER || res.data?.data;
      if (user) {
        setAvailableDeposit(parseFloat(user.available_deposit || user.available_amount || 0));
      }
    } catch (e) {
      console.error('Profile fetch error:', e);
    }
  };

  const fetchActiveStakes = async () => {
    setLoading(true);
    try {
      const response = await postApi('stake-list');
      if (response.data?.status === true) {
        setStakedata(response.data.data);
      } else {
        toast.error('Failed to load stakes');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (stake) => { setSelectedStake(stake); setShowDetailsModal(true); };

  const submitStakeAmount = async () => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      toast.error('Please enter a valid stake amount');
      return;
    }
    if (parseFloat(stakeAmount) > availableDeposit) {
      toast.error('Insufficient available deposit balance');
      return;
    }
    setSubmitting(true);
    try {
      const response = await postApi('stake', {
        stake_amount: stakeAmount,
        wallet_type: 'available',
        plan: 'Gold',
      });
      if (response?.data?.status) {
        toast.success(response.data.message || 'Stake Successful! 🎉');
        setShowStakeModal(false);
        setStakeAmount('');
        fetchActiveStakes();
        fetchProfile();
      } else {
        toast.error(response?.data?.message || 'Failed to stake');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again!');
    } finally {
      setSubmitting(false);
    }
  };

  const closeStakeModal = () => {
    if (!submitting) { setShowStakeModal(false); setStakeAmount(''); }
  };

  return (
    <div className="page-wrapper">
      {/* ── Header ── */}
      <div className="page-header mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <div className="section-label">Earn Passive Income</div>
          <h2 className="page-title">Staked : $ {stakedata?.stake_amount || 0}</h2>
          <p className="page-subtitle">Stake your assets and earn rewards every day</p>
        </div>
        <button className="btn btn-gradient" onClick={() => setShowStakeModal(true)}>
          <i className="bi bi-plus-circle me-2"></i>Stake Now
        </button>
      </div>

      {/* ── Analytics Cards ── */}
      <div className="row g-3 mb-4">
        {analytics.map((item, i) => (
          <div key={i} className="col-lg-3 col-md-6">
            <div className={`metric-card ${item.color}`}>
              <div className={`metric-icon ${item.color}`}>
                <i className={`bi ${item.icon}`}></i>
              </div>
              <div>
                <div className="metric-label">{item.title}</div>
                <div className="metric-value">
                  {loading
                    ? <span className="skeleton d-inline-block" style={{ width: 80, height: 22 }}></span>
                    : item.value}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Active Stakes Table ── */}
      <div className="p-card">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <p className="section-label mb-0">Portfolio</p>
            <h5 style={{ margin: 0, fontWeight: 700 }}>Active Stakes</h5>
          </div>
          <button className="btn btn-sm" onClick={fetchActiveStakes}
            style={{ background: 'rgba(0,255,149,0.1)', color: 'var(--page-accent)', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 10 }}>
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
          </button>
        </div>

        <div className="p-table-wrap">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-success mb-3"></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading your stakes...</p>
            </div>
          ) : (stakedata?.data || []).length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><i className="bi bi-layers"></i></div>
              <h6>No Active Stakes</h6>
              <p>Start staking to earn daily rewards</p>
              <button className="btn btn-gradient mt-3" onClick={() => setShowStakeModal(true)}>
                <i className="bi bi-plus-circle me-2"></i>Stake Now
              </button>
            </div>
          ) : (
            <table className="p-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Amount</th>
                  <th>Daily %</th>
                  <th>Daily Credit</th>
                  <th>Total Credit</th>
                  <th>Age</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(stakedata?.data || []).map((stake, index) => (
                  <tr key={stake.id}>
                    <td className="dim">{index + 1}</td>
                    <td><span style={{ fontWeight: 700, color: 'var(--page-accent)' }}>${stake.stake_amount}</span></td>
                    <td>{stake.daily_reward_percentage}%</td>
                    <td className="mono">${stake.daily_credit_reward}</td>
                    <td className="mono" style={{ color: 'var(--page-blue)' }}>${stake.total_credit}</td>
                    <td className="dim">{stake.stake_age}</td>
                    <td className="dim" style={{ fontSize: '0.8rem' }}>{stake.date_time}</td>
                    <td>
                      <span className={`s-pill ${(stake.stake_status || 'active').toLowerCase()}`}>
                        {stake.stake_status || 'Active'}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm" onClick={() => openDetails(stake)}
                        style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
                        <i className="bi bi-eye me-1"></i>View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ══ STAKE MODAL ══ */}
      {showStakeModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeStakeModal(); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: 'linear-gradient(135deg,#0a1628,#071018)', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24 }}>

              {/* Modal Header */}
              <div className="modal-header border-0 pb-0 px-4 pt-4">
                <div>
                  <p className="section-label mb-0">New Position</p>
                  <h4 className="fw-800 mb-0" style={{ color: 'white' }}>Create Stake</h4>
                </div>
                <button className="btn-close btn-close-white" onClick={closeStakeModal}></button>
              </div>

              <div className="modal-body px-4 pb-2 pt-3">

                {/* ── Available Balance ── */}
                <div style={{
                  background: 'rgba(0,255,149,0.06)',
                  border: '1px solid rgba(0,255,149,0.18)',
                  borderRadius: 14,
                  padding: '0.9rem 1.1rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <i className="bi bi-wallet2" style={{ color: 'var(--page-accent)', fontSize: '1.1rem' }}></i>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Available Deposit</div>
                      <div style={{ fontWeight: 800, fontSize: '1.15rem', fontFamily: 'JetBrains Mono, monospace', color: 'var(--page-accent)' }}>
                        ${availableDeposit.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Plan</div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'gold' }}>🏆 Gold</div>
                  </div>
                </div>

                {/* ── Amount Input ── */}
                <p className="p-label mb-2">Enter Amount</p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.1)',
                  overflow: 'hidden',
                  marginBottom: 6,
                }}>
                  <span style={{ background: 'transparent', border: 'none', color: 'var(--page-accent)', padding: '0 1.2rem', fontWeight: 700, fontSize: '1.1rem' }}>$</span>
                  <input
                    type="number"
                    style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.3rem', padding: '0.9rem 0', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, flex: 1, outline: 'none' }}
                    placeholder="0.00"
                    value={stakeAmount}
                    min="10"
                    step="0.01"
                    onChange={e => setStakeAmount(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setStakeAmount(availableDeposit.toString())}
                    style={{ background: 'rgba(0,255,149,0.1)', border: 'none', borderLeft: '1px solid rgba(0,255,149,0.2)', color: 'var(--page-accent)', padding: '0 1.1rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', height: '100%' }}
                  >
                    MAX
                  </button>
                </div>

                {/* Validation hint */}
                {stakeAmount && parseFloat(stakeAmount) > availableDeposit && (
                  <p style={{ color: 'var(--page-danger)', fontSize: '0.78rem', margin: '4px 0 8px' }}>
                    <i className="bi bi-exclamation-circle me-1"></i>Exceeds available deposit
                  </p>
                )}
                {stakeAmount && parseFloat(stakeAmount) < 10 && parseFloat(stakeAmount) > 0 && (
                  <p style={{ color: 'var(--page-warning)', fontSize: '0.78rem', margin: '4px 0 8px' }}>
                    <i className="bi bi-info-circle me-1"></i>Minimum stake is $10
                  </p>
                )}

                {/* ── Plan Preview Card ── */}
                {preview && parseFloat(stakeAmount) >= 10 && (
                  <div style={{
                    background: 'rgba(0,255,149,0.05)',
                    borderRadius: 16,
                    border: '1px solid rgba(0,255,149,0.2)',
                    padding: '1rem 1.2rem',
                    margin: '1rem 0',
                    boxShadow: '0 0 20px rgba(0,255,149,0.04)',
                  }}>
                    {/* Card header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.85rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>🏆</span>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Gold Plan</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                          Capping: {preview.capping}x &nbsp;·&nbsp; Period: {preview.period}
                        </div>
                      </div>
                    </div>

                    {/* 4-cell grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                      {[
                        { label: 'Plan', value: preview.plan, accent: false },
                        { label: 'ROI', value: preview.roi, accent: true },
                        { label: 'Est. Daily Reward', value: `$${preview.dailyCredit}`, accent: true },
                        { label: 'Period', value: preview.period, accent: false },
                      ].map((r, i) => (
                        <div key={i} style={{
                          background: 'rgba(255,255,255,0.04)',
                          borderRadius: 10,
                          padding: '0.55rem 0.75rem',
                          border: '1px solid rgba(255,255,255,0.07)',
                        }}>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: 3 }}>{r.label}</div>
                          <div style={{
                            fontWeight: 800,
                            fontSize: '0.88rem',
                            fontFamily: 'JetBrains Mono, monospace',
                            color: r.accent ? 'var(--page-accent)' : 'white',
                          }}>{r.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Total income row */}
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.6rem 0.8rem',
                      background: 'rgba(0,255,149,0.07)',
                      borderRadius: 10,
                      border: '1px solid rgba(0,255,149,0.15)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Total Estimated Income</span>
                      <span style={{ fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: 'var(--page-accent)', fontSize: '0.95rem' }}>
                        ${preview.totalIncome}
                      </span>
                    </div>
                  </div>
                )}

                {/* ── Info bullets ── */}
                <div style={{ background: 'rgba(247,201,72,0.06)', borderRadius: 12, padding: '0.85rem 1rem', border: '1px solid rgba(247,201,72,0.15)' }}>
                  {[
                    'Minimum stake $10 USDT',
                    'Rewards credited daily',
                    'Capping 2x for ≤$3000, 2.5x for >$3000',
                    'Rewards paid over 720 days',
                  ].map((t, i) => (
                    <p key={i} className="mb-1" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                      <span style={{ color: 'var(--page-warning)', marginRight: 6 }}>●</span>{t}
                    </p>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="modal-footer border-0 px-4 pb-4 gap-2">
                <button className="btn" onClick={closeStakeModal}
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '0.75rem 1.5rem', flex: 1 }}>
                  Cancel
                </button>
                <button
                  className="btn btn-gradient"
                  disabled={submitting || !stakeAmount || parseFloat(stakeAmount) < 10 || parseFloat(stakeAmount) > availableDeposit}
                  onClick={submitStakeAmount}
                  style={{ flex: 2, borderRadius: 12, padding: '0.75rem', fontWeight: 700 }}>
                  {submitting
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Staking...</>
                    : <><i className="bi bi-layers me-2"></i>Confirm Stake</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ DETAILS MODAL ══ */}
      {showDetailsModal && selectedStake && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#0a1628', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24 }}>
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="fw-700 mb-0" style={{ color: 'white' }}>
                  <i className="bi bi-layers me-2" style={{ color: 'var(--page-accent)' }}></i>Stake Details
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                <div className="text-center mb-4">
                  <div style={{ fontSize: '3rem', marginBottom: 8 }}>🏆</div>
                  <span className="s-pill active">{selectedStake.stake_status || 'Active'}</span>
                </div>
                {[
                  { label: 'Staked Amount', value: `$${selectedStake.stake_amount}`, color: 'var(--page-accent)' },
                  { label: 'Daily Reward %', value: `${selectedStake.daily_reward_percentage}%`, color: 'var(--page-blue)' },
                  { label: 'Daily Credit', value: `$${selectedStake.daily_credit_reward}`, color: null },
                  { label: 'Total Credit So Far', value: `$${selectedStake.total_credit}`, color: 'var(--page-accent)' },
                  { label: 'Stake Age', value: selectedStake.stake_age, color: null },
                  { label: 'Last Updated', value: selectedStake.last_update, color: null },
                ].map((d, i) => (
                  <div key={i} className="info-row">
                    <span className="i-label">{d.label}</span>
                    <span className="i-value" style={{ color: d.color || 'white', fontFamily: 'JetBrains Mono, monospace' }}>{d.value}</span>
                  </div>
                ))}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button className="btn w-100" onClick={() => setShowDetailsModal(false)}
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '0.75rem' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stake;
